import {
    mdiAccountPlusOutline,
    mdiAccountRemoveOutline,
    mdiCheck,
    mdiChevronDown,
} from '@mdi/js';
import Icon from '@mdi/react';
import React, { useMemo, useState } from 'react';
import { Popover, Spinner, Variables } from '../base';
import * as S from './assignee-picker.styles';

export interface IAssigneeOption {
    id: string;
    name: string;
    role?: string;
    isYou?: boolean;
    color: string;
}

export interface IAssigneePickerProps {
    selectedId?: string | null;
    options: IAssigneeOption[];
    loading?: boolean;
    saving?: boolean;
    disabled?: boolean;
    onAssign: (userId: string | null) => void | Promise<void>;
}

const getInitials = (name: string): string => {
    if (!name) return '?';
    const parts = name.trim().split(/\s+/).filter(Boolean);
    if (parts.length >= 2) {
        return `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase();
    }
    return name.slice(0, 2).toUpperCase();
};

const firstName = (name: string): string => name.trim().split(/\s+/)[0] || name;

export const AssigneePicker: React.FC<IAssigneePickerProps> = ({
    selectedId,
    options,
    loading = false,
    saving = false,
    disabled = false,
    onAssign,
}) => {
    const [open, setOpen] = useState(false);

    const selected = useMemo(
        () => (selectedId ? options.find(o => o.id === selectedId) : undefined),
        [options, selectedId],
    );

    const handleSelect = async (userId: string | null) => {
        if (saving || disabled) {
            return;
        }
        if (userId === (selectedId || null)) {
            setOpen(false);
            return;
        }
        setOpen(false);
        await onAssign(userId);
    };

    const triggerLabel = selected
        ? selected.isYou
            ? 'You'
            : firstName(selected.name)
        : 'Unassigned';

    return (
        <Popover
            open={open}
            onOpenChange={next => {
                if (!disabled && !saving) {
                    setOpen(next);
                }
            }}
            align="right"
            verticalAlign="bottom"
            renderButton={() => (
                <S.Trigger
                    type="button"
                    variant="unstyled"
                    disabled={disabled || saving}
                    aria-label="Change who this task is assigned to">
                    {selected ? (
                        <S.Avatar bg={selected.color} isYou={selected.isYou} size={22}>
                            {getInitials(selected.name)}
                        </S.Avatar>
                    ) : (
                        <S.EmptyAvatar>
                            <Icon path={mdiAccountPlusOutline} size={0.55} color={Variables.ThemeLight.TextMild} />
                        </S.EmptyAvatar>
                    )}
                    <S.TriggerLabel>{saving ? 'Saving…' : triggerLabel}</S.TriggerLabel>
                    <Icon path={mdiChevronDown} size={0.65} color={Variables.ThemeLight.TextMild} />
                </S.Trigger>
            )}>
            <S.Menu>
                <S.MenuHeader>Assign to</S.MenuHeader>
                {(loading || saving) && (
                    <S.LoadingRow>
                        <Spinner size="16px" />
                    </S.LoadingRow>
                )}
                {!loading && !saving && (
                    <S.Options>
                        {options.map(option => {
                            const isSelected = option.id === selectedId;
                            return (
                                <S.OptionButton
                                    key={option.id}
                                    type="button"
                                    selected={isSelected}
                                    onClick={() => handleSelect(option.id)}>
                                    <S.Avatar bg={option.color} isYou={option.isYou} size={24}>
                                        {getInitials(option.name)}
                                    </S.Avatar>
                                    <S.OptionText>
                                        <S.OptionName>
                                            {option.name}
                                            {option.isYou ? ' (you)' : ''}
                                        </S.OptionName>
                                        {option.role && <S.OptionRole>{option.role}</S.OptionRole>}
                                    </S.OptionText>
                                    {isSelected && (
                                        <Icon
                                            path={mdiCheck}
                                            size={0.7}
                                            color={Variables.ThemeLight.PrimaryConstant6}
                                        />
                                    )}
                                </S.OptionButton>
                            );
                        })}
                        {options.length === 0 && (
                            <S.OptionButton type="button" disabled>
                                <S.OptionName>No users</S.OptionName>
                            </S.OptionButton>
                        )}
                        {!!selectedId && (
                            <S.UnassignButton type="button" onClick={() => handleSelect(null)}>
                                <Icon
                                    path={mdiAccountRemoveOutline}
                                    size={0.7}
                                    color={Variables.ThemeLight.TextMild}
                                />
                                Unassign
                            </S.UnassignButton>
                        )}
                    </S.Options>
                )}
            </S.Menu>
        </Popover>
    );
};

/** Stable palette for assignee avatars (matches redesign mock tones). */
export const assigneeAvatarColor = (seed: string): string => {
    const palette = [
        Variables.ThemeLight.PrimaryConstant6,
        Variables.ThemeLight.SuccessConstant6,
        Variables.ThemeLight.AttentionConstant6,
        Variables.ThemeLight.TextMild,
        Variables.ThemeLight.WarningConstant6,
    ];
    let hash = 0;
    for (let i = 0; i < seed.length; i += 1) {
        hash = (hash * 31 + seed.charCodeAt(i)) | 0;
    }
    return palette[Math.abs(hash) % palette.length];
};

export const formatAssigneeRole = (roleId?: string): string | undefined => {
    if (!roleId) {
        return undefined;
    }
    return roleId
        .toLowerCase()
        .split('_')
        .map(part => part.charAt(0).toUpperCase() + part.slice(1))
        .join(' ');
};
